import { SingleSelectFieldFilter } from 'components/organisms/tasks/filters/single-select';
import { TaskCompletionType } from 'components/organisms/tasks/views';
import { FilterGroupType } from 'components/pages/types';
import type { Maybe } from 'core/maybe';
import { Just, Nothing, ofNullable, orElse, valueOf } from 'core/maybe';
import { QueryCombinerType } from '../combiner';
import {
  AssigneeField,
  AssigneeValue,
  DateField,
  FieldType,
  FilterableField,
  MultiSelectField,
  SingleSelectField,
  SingleUserField,
} from '../fields';
import { FieldFilter } from '../filters';
import { AssigneeFieldFilter } from '../filters/assignee';
import { DateFieldFilter, DateFieldFilterOperationType } from '../filters/date';
import { MultiSelectFieldFilter } from '../filters/multi-select';
import { SelectFilterOperationType } from '../filters/select';
import { SingleUserFieldFilter } from '../filters/single-user';
import { GroupedFilter } from '../grouped-filter-popover';
import { computeShowTaskCriteria, parseShowTaskCriteria } from './utils';

export interface SimpleGroupedFilter {
  assignee: AssigneeValue[];
  responsible: MultiSelectFieldFilter;
  project: number[];
  dueDate: DateFieldFilter;
  createdBy: Maybe<number>;
  status: TaskCompletionType;
}

const EMPTY: SimpleGroupedFilter = {
  assignee: [],
  responsible: {
    field: {
      fieldName: 'responsible',
      fieldLabel: 'Responsible',
      type: FieldType.MultiSelect,
      metaData: {},
    } as any,
    type: SelectFilterOperationType.IsOneOf,
  },
  project: [],
  dueDate: {
    field: { fieldName: 'dueDate', type: FieldType.Date, metaData: {} } as any,
    type: DateFieldFilterOperationType.IsAny,
  },
  createdBy: Nothing(),
  status: TaskCompletionType.All,
};

const tryParse = (
  filter: GroupedFilter,
  lookup: Record<string, FilterableField>,
): Maybe<SimpleGroupedFilter> => {
  const criteria = filter.groupCriteria.filter((x) =>
    FieldFilter.isValid(x.criteria),
  );
  const allowed = Object.keys(EMPTY);

  if (
    criteria.length > allowed.length ||
    criteria.some((x) => !allowed.includes(x.criteria.field.fieldName))
  ) {
    return Nothing();
  }
  const find = <T extends FieldFilter>(fieldName: string) =>
    criteria
      .filter((x) => x.criteria.field.fieldName === fieldName)
      .map((x) => x.criteria) as T[];
  const assigneeCriteria = find<AssigneeFieldFilter>('assignee');

  if (assigneeCriteria.length > 1) {
    return Nothing();
  }
  const responsibleCriteria = find<MultiSelectFieldFilter>('responsible');

  if (responsibleCriteria.length > 1) {
    return Nothing();
  }
  const projectCriteria = find<SingleSelectFieldFilter>('project');

  if (projectCriteria.length > 1) {
    return Nothing();
  }
  const dueDateCriteria = find<DateFieldFilter>('dueDate');

  if (dueDateCriteria.length > 1) {
    return Nothing();
  }

  const createdByCriteria = find<SingleUserFieldFilter>('createdBy');

  if (createdByCriteria.length > 1) {
    return Nothing();
  }

  const statusCriteria = find<SingleSelectFieldFilter>('status');

  if (statusCriteria.length > 1) {
    return Nothing();
  }

  const assigneeCriterion = assigneeCriteria[0];

  if (
    assigneeCriterion != null &&
    assigneeCriterion.type !== SelectFilterOperationType.IsOneOf
  ) {
    return Nothing();
  }
  const responsibleCriterion = responsibleCriteria[0];

  if (
    responsibleCriterion != null &&
    responsibleCriterion.type !== SelectFilterOperationType.IsOneOf
  ) {
    return Nothing();
  }
  const projectCriterion = projectCriteria[0];

  if (
    projectCriterion != null &&
    projectCriterion.type !== SelectFilterOperationType.IsOneOf
  ) {
    return Nothing();
  }

  const createdByCriterion = createdByCriteria[0];

  if (
    createdByCriterion != null &&
    createdByCriterion.type !== SelectFilterOperationType.Is
  ) {
    return Nothing();
  }

  const assignee = assigneeCriterion?.value ?? [];
  const responsible = responsibleCriterion ?? EMPTY.responsible;
  const project = projectCriterion?.value ?? [];
  const createdBy = ofNullable(createdByCriterion?.value ?? null);
  const dueDate = dueDateCriteria[0] ?? EMPTY.dueDate;
  let { status } = EMPTY;

  if (statusCriteria[0]) {
    const parsedStatusCriteria = parseShowTaskCriteria(
      statusCriteria[0],
      lookup,
    );

    // If the parsed criteria is not in simple filter list, show advanced mode
    if (!parsedStatusCriteria) {
      return Nothing();
    }
    status = parsedStatusCriteria;
  }

  return Just({
    assignee,
    responsible,
    dueDate,
    createdBy,
    project,
    status,
  });
};

const empty = (): SimpleGroupedFilter => EMPTY;

const toFilter =
  (fields_lookup: Record<string, FilterableField>) =>
  ({
    dueDate,
    assignee,
    createdBy,
    project,
    responsible,
    status,
  }: SimpleGroupedFilter): GroupedFilter => {
    const showTaskCriteria = computeShowTaskCriteria(fields_lookup, status);

    return {
      type: QueryCombinerType.All,
      defaultGroup: FilterGroupType.Task,
      groupCriteria: (
        [
          {
            field: fields_lookup.assignee as AssigneeField,
            type: SelectFilterOperationType.IsOneOf,
            value: assignee,
          },
          {
            ...responsible,
            field: fields_lookup.responsible as MultiSelectField,
          },
          ...(fields_lookup.project != null
            ? [
                {
                  field: fields_lookup.project as SingleSelectField,
                  type: SelectFilterOperationType.IsOneOf,
                  value: project,
                },
              ]
            : []),
          { ...dueDate, field: fields_lookup.dueDate as DateField },
          {
            field: fields_lookup.createdBy as SingleUserField,
            type: SelectFilterOperationType.Is,
            value: valueOf(createdBy) ?? undefined,
          },
          ...showTaskCriteria,
        ] as FieldFilter[]
      )
        .filter(FieldFilter.isValid)
        .map((x) => ({
          id: FilterGroupType.Task,
          criteria: x,
        })),
    };
  };

const parse = (
  filter: GroupedFilter,
  lookup: Record<string, FilterableField>,
): SimpleGroupedFilter => orElse(empty())(tryParse(filter, lookup));

export const SimpleGroupedFilter = {
  tryParse,
  parse,
  empty,
  toFilter,
};
